import React, { Component } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Paper,
    Divider,
    Chip,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    OutlinedInput,
    Checkbox,
    ListItemText,
  } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { alpha } from "@mui/material/styles";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const QUESTION_OPTIONS = [
  { key: "spent_more", label: "Spending Rates" },
  { key: "saved_more", label: "Saving Rates" },
  { key: "income_change", label: "Disposable Income" },
  { key: "personal_six_months", label: "Personal Financial Outlook (6 Months)" },
  { key: "country_six_months", label: "Country Economic Outlook (6 Months)" },
  { key: "security", label: "Job Security" },
  { key: "jobseek", label: "Job Searching" },
];
const DEMO_ALLOWED_QUESTION = QUESTION_OPTIONS[0].key;

const QUESTION_TEXT = [
    { key: "spent_more", label: "Overall, to what extent do you agree or disagree with the following statement: I spent more overall in the last month compared to the month prior" },
    { key: "saved_more", label: "Overall, to what extent do you agree or disagree with the following statement: I saved more money in the last month compared to the month prior" },
    { key: "income_change", label: "Over the past month, has your disposable income (the money left after taxes) increased, decreased, or stayed the same?" },
    { key: "personal_six_months", label: "To what extent do you expect the following to improve or worsen over the next 6 months: Your personal financial situation" },
    { key: "country_six_months", label: "To what extent do you expect the following to improve or worsen over the next 6 months: The UK's economic situation" },
    { key: "security", label: "Compared to last month, how secure do you feel in your current job?" },
    { key: "jobseek", label: "Do you plan to actively look for a new job in the next 3 months?" },
  ];

  const LIKERT_COLOURS = {
    "Strongly agree": "#1b7837",
    "Somewhat agree": "#7fbf7b",
    "Neither agree nor disagree": "#bdbdbd",
  
    "Don't know": "#d9d9d9",
    "Somewhat disagree": "#ef8a62",
    "Strongly disagree": "#b2182b",
  
    "Increased significantly": "#1b7837",
    "Increased slightly": "#7fbf7b",
    "Stayed the same": "#bdbdbd",
    "Decreased slightly": "#ef8a62",
    "Decreased significantly": "#b2182b",
  
    "Improve a lot": "#1b7837",
    "Improve a little": "#7fbf7b",
    "Neither": "#bdbdbd",
    "Worsen a little": "#ef8a62",
    "Worsen a lot": "#b2182b",
  
    "Much more secure": "#1b7837",
    "Somewhat more secure": "#7fbf7b",
    "About the same": "#bdbdbd",
    "Somewhat less secure": "#ef8a62",
    "Much less secure": "#b2182b",

      // NEW: jobseek colors
    "Yes, I am actively looking": "#b2182b",
    "Maybe, depending on circumstances": "#bdbdbd",
    "No, I am not planning to look": "#1b7837",
  
    "Net value": "#000000",
  };
  

  const RESPONSE_ORDER = [
    "Strongly agree",
    "Somewhat agree",
    "Neither agree nor disagree",
    "Somewhat disagree",
    "Strongly disagree",
  
    "Increased significantly",
    "Increased slightly",
    "Stayed the same",
    "Decreased slightly",
    "Decreased significantly",

  
    "Improve a lot",
    "Improve a little",
    "Neither",
    "Worsen a little",
    "Worsen a lot",
    
  
    "Much more secure",
    "Somewhat more secure",
    "About the same",
    "Somewhat less secure",
    "Much less secure",

    "Yes, I am actively looking",
    "Maybe, depending on circumstances",
    "No, I am not planning to look",
    "Don't know",

    "Net value",
  ];
  


  const POSITIVE_TERMS = [
    "Strongly agree",
    "Somewhat agree",
    "Increased significantly",
    "Increased slightly",
    "Improve a lot",
    "Improve a little",
    "Much more secure",
    "Somewhat more secure",
  ];
  
  const NEGATIVE_TERMS = [
    "Strongly disagree",
    "Somewhat disagree",
    "Decreased significantly",
    "Decreased slightly",
    "Worsen a lot",
    "Worsen a little",
    "Much less secure",
    "Somewhat less secure",
  ];
  
  const QUESTIONS_WITH_NET = [
    "spent_more",
    "saved_more",
    "income_change",
    "personal_six_months",
    "country_six_months",
    "security",
  ];


const SPLIT_OPTIONS = [
    "age",
    "gender",
    "region",
    "education",
    "tenure",
    "children",
    "past_vote_2024",
    "income",
    "employment",
  ];

  const DISPLAY_LABELS = {
    "age": "Age",
    "gender": "Gender",
    "region": "Region",
    "education": "Education",
    "tenure": "Tenure",
    "children": "Children",
    "past_vote_2024": "Past Vote 2024", 
    "income": "Income",
    "employment": "Employment",
};
  
  const ALL_KEY = "All respondents";
  const palette = [
    "#2962FF","#D81B60","#43A047","#FF8F00","#6D4C41","#00838F","#5E35B1","#C62828",
    "#2E7D32","#AD1457","#0277BD","#F9A825","#7B1FA2","#0097A7","#8D6E63","#EF6C00",
    "#1E88E5","#9E9D24","#00897B","#AB47BC"
  ];

  
  export default class IndicatorsPage extends Component {
    state = {
      view: "breakdown", // "breakdown" | "net"
      question: "spent_more",
      filters: {},
      filterOptions: {},
      data: [],
      loading: false,
      sampleSize:[],
      // net-comparison
      splitBy: "age",
      allSubgroups: [],
      visibleSubgroups: [],
      chartDataNet: [],
      colorMap: {},
    };
  
    async componentDidMount() {
      if (this.props.isDemoUser && this.state.question !== DEMO_ALLOWED_QUESTION) {
        this.setState({ question: DEMO_ALLOWED_QUESTION });
      }
      await this.fetchFilterOptions();
      this.fetchData(this.props.isDemoUser ? DEMO_ALLOWED_QUESTION : this.state.question);
      this.loadNetData();
    }

  fetchFilterOptions = async () => {
    try {
      const res = await fetch("/api/indicators_filter_options/");
      const json = await res.json();
      this.setState({ filterOptions: json });
    } catch (e) {
      console.error("filter options error", e);
    }
  };
  fetchData = async (question = this.state.question, filters = this.state.filters) => {
    this.setState({ loading: true });
    try {
      const res = await fetch("/api/indicators_data/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, filters }),
      });
      const json = await res.json();

      const byWave = {};
      json.data.forEach((d) => {
        const wave = d.Wave;
        const date = d.Date;
        const label = `${date}`;
        if (!byWave[label]) byWave[label] = { wave: label };
        byWave[label][d.response] = d.pct;
      });

      const combined = Object.values(byWave).sort(
        (a, b) =>
          parseInt(a.wave.replace(/\D/g, "")) -
          parseInt(b.wave.replace(/\D/g, ""))
      );

      if (QUESTIONS_WITH_NET.includes(question)) {
        combined.forEach((row) => {
        const pos = POSITIVE_TERMS.reduce((sum, cat) => sum + (row[cat] || 0), 0);
        const neg = NEGATIVE_TERMS.reduce((sum, cat) => sum + (row[cat] || 0), 0);
        row["Net value"] = Math.round(pos - neg)
      });
    }

    const cleaned = this.cleanChartRows(combined);

    this.setState({
      data: cleaned,
      loading: false,
      sampleSize: json.sample_size,
    });

    } catch (e) {
      console.error(e);
      this.setState({ loading: false });
    }
  };


  loadNetData = async (resetSelection = false) => {
    const { question, splitBy } = this.state;
    if (!QUESTIONS_WITH_NET.includes(question)) return;
  
    try {
      const res = await fetch("/api/indicators_net_by_demo/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, split_by: splitBy }),
      });
      const json = await res.json();
      const byKey = {};
      let netLabel = null; 
  
      (json.series || []).forEach((s) => {
        const label = s.subgroup;
        (s.data || []).forEach((row) => {
          const x = row.Date;
          if (!byKey[x]) byKey[x] = { wave: x };
          byKey[x][label] = Number(row.net);
          if (!netLabel && row.label) netLabel = row.label; 
        });
      });
  
      const chartDataNet = Object.values(byKey);
      const allSubgroups = (json.subgroups || []).filter(Boolean);
      const colorMap = {};
      [ALL_KEY, ...allSubgroups].forEach((sg, i) => {
        colorMap[sg] = sg === ALL_KEY ? "#000" : palette[i % palette.length];
      });
      const cleanedNet = this.cleanChartRows(chartDataNet);

      this.setState({
        chartDataNet: cleanedNet,
        allSubgroups,
        visibleSubgroups: resetSelection
        ? allSubgroups              // split changed → reset to all
        : this.state.visibleSubgroups.length
            ? this.state.visibleSubgroups.filter((sg) => allSubgroups.includes(sg))
            : allSubgroups,

            

        colorMap,
        netLabel: netLabel || "Net value",
      });
      
    } catch (e) {
      console.error("net data fetch error", e);
    }
  };
  

  handleQuestionChange = (e) => {
    const question = e.target.value;
    if (this.props.isDemoUser && question !== DEMO_ALLOWED_QUESTION) return;
    this.setState({ question }, () => {
      this.fetchData(question);
      if (QUESTIONS_WITH_NET.includes(question)) this.loadNetData();
    });
  };

  handleFilterChange = (key, val) => {
    this.setState(
      (prev) => ({ filters: { ...prev.filters, [key]: val } }),
      () => this.fetchData(this.state.question, this.state.filters)
    );
  };
  cleanChartRows(rows) {
    return rows.filter((row) => {
      const numericValues = Object.entries(row)
        .filter(([key]) => key !== "wave")
        .map(([, value]) =>
          typeof value === "number" && isFinite(value) ? value : null
        );
  
      // remove rows with zero valid numeric values
      const validValues = numericValues.filter((v) => v !== null);
  
      if (validValues.length === 0) return false; // all nulls
  
      // NEW RULE: remove rows where all subgroup values are EXACTLY 0
      const allZero = validValues.every((v) => v === 0);
  
      return !allZero;
    });
  }
  
  
  renderActiveFilters() {
    const active = Object.entries(this.state.filters).filter(
      ([, v]) => v && v.trim() !== ""
    );
    if (!active.length)
      return <Typography color="text.secondary">All respondents</Typography>;
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {active.map(([key, val]) => (
          <Chip
            key={key}
            label={`${key}: ${val}`}
            size="small"
            sx={{
              bgcolor: alpha("#1976d2", 0.08),
              color: "primary.main",
              fontWeight: 500,
            }}
          />
        ))}
      </Stack>
    );
  }


  render() {
    const {
      view,
      question,
      data,
      loading,
      splitBy,
      allSubgroups,
      visibleSubgroups,
      colorMap,
      chartDataNet,
    } = this.state;
        const formatKeyLabel = (key) => {
          return key
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        };
        const questionLabel =
          QUESTION_OPTIONS.find((q) => q.key === question)?.label || question;
        const questionText =
            QUESTION_TEXT.find((q) => q.key === question)?.label || question;
        const allValues = data.flatMap((d) =>
            Object.entries(d)
                .filter(([k]) => k !== "wave" && typeof d[k] === "number" && isFinite(d[k]))
                .map(([, v]) => v)
            );
            
            const minY = Math.min(...allValues);
            const maxY = Math.max(...allValues);
            
let yDomain;

if (view === "net") {
    const numericValues = chartDataNet.flatMap((d) =>
      Object.entries(d)
        .filter(([k, v]) => k !== "wave")
        .map(([, v]) => {
          if (typeof v === "string") {
            const num = parseFloat(v.replace(/[%\s,]/g, ""));
            return isNaN(num) ? null : num;
          }
          return typeof v === "number" && isFinite(v) ? v : null;
        })
        .filter((v) => v !== null)
    );
  
    const minY = numericValues.length ? Math.min(...numericValues) : 0;
    const maxY = numericValues.length ? Math.max(...numericValues) : 0;
  
    yDomain = [Math.floor(minY - 5), Math.ceil(maxY + 5)];
  }
  
  else {
  const allValues = data.flatMap((d) =>
    Object.entries(d)
      .filter(([k]) => k !== "wave" && typeof d[k] === "number" && isFinite(d[k]))
      .map(([, v]) => v)
  );
  const minY = Math.min(...allValues);
  const maxY = Math.max(...allValues);
  yDomain =
    minY < 0
      ? [Math.floor(minY - 5), Math.ceil(maxY + 5)]
      : [Math.max(0, Math.floor(minY - 5)), Math.min(100, Math.ceil(maxY + 5))];
}


    return (
      <Box sx={{ display: "flex", height: "92vh", p: 2, gap: 2, bgcolor: "#f7f8fa" }}>
        <Paper
  elevation={3}
  sx={{
    width: 340,
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    overflowY: "auto",
    borderRadius: 3,
    bgcolor: "#fff",
  }}
>
  <Box
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 10,
      bgcolor: "#fff",
      p: 3,
      borderBottom: "1px solid #eee",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}
  >
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, mb: 2, color: "text.primary" }}
    >
      Indicators Dashboard
    </Typography>

    <ToggleButtonGroup
      color="primary"
      exclusive
      value={view}
      onChange={(e, newView) => newView && this.setState({ view: newView })}
      sx={{
        display: "flex",
        justifyContent: "center",
        mb: 2,
        "& .MuiToggleButton-root": {
          flex: 1,
          borderRadius: "20px !important",
          textTransform: "none",
          fontWeight: 600,
          fontSize: 14,
          py: 1,
        },
        "& .Mui-selected": {
          bgcolor: "primary.main",
          color: "white",
          "&:hover": { bgcolor: "primary.dark" },
        },
      }}
    >
      <ToggleButton value="breakdown">Breakdown</ToggleButton>
      <ToggleButton value="net">Net Comparison</ToggleButton>
    </ToggleButtonGroup>

    <FormControl fullWidth>
      <InputLabel>Question</InputLabel>
      <Select
        value={question}
        label="Question"
        onChange={this.handleQuestionChange}
      >
        {QUESTION_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.key}
            value={opt.key}
            disabled={Boolean(this.props.isDemoUser) && opt.key !== DEMO_ALLOWED_QUESTION}
          >
            <Box sx={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
              <span>{opt.label}</span>
              {Boolean(this.props.isDemoUser) && opt.key !== DEMO_ALLOWED_QUESTION && (
                <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
          <Box sx={{ p: 3, pt: 2 }}>
{view === "net" && QUESTIONS_WITH_NET.includes(question) && (
            <>
             <Divider sx={{ my: .5 }} />
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
    >
      Compare demographic groups
    </Typography>
              <FormControl fullWidth sx={{ mt:1, mb: 3 }}>
                <InputLabel>Split by</InputLabel>
                <Select
              value={splitBy}
              label="Split by"
              onChange={(e) =>
                this.setState({ splitBy: e.target.value }, () =>
                  this.loadNetData(true)   // reset subgroup selection
                )
              }            >
              {SPLIT_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {DISPLAY_LABELS[opt]}
                </MenuItem>
              ))}
            </Select>
              </FormControl>
            
              <Typography
  variant="subtitle2"
  sx={{ fontWeight: 500, color: "text.primary", mb: 1, mt: 2 }}
>
  Toggle Subgroups
</Typography>

<Box
  sx={{
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
    mb: 2,
  }}
>
  {allSubgroups.map((name) => {
    const selected = visibleSubgroups.includes(name);
    return (
      <Chip
        key={name}
        label={name}
        onClick={() => {
          this.setState((prev) => {
            const visible = prev.visibleSubgroups.includes(name)
              ? prev.visibleSubgroups.filter((s) => s !== name)
              : [...prev.visibleSubgroups, name];
            return { visibleSubgroups: visible };
          });
        }}
        variant={selected ? "filled" : "outlined"}
        color={selected ? "primary" : "default"}
        sx={{
          fontWeight: selected ? 600 : 400,
          borderRadius: "20px",
          px: 1,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: selected ? "primary.dark" : "rgba(0,0,0,0.04)",
          },
        }}
      />
    );
  })}
</Box>
<Stack direction="row" spacing={1} sx={{ mb: 1 }}>
  <Chip
    label="Select All"
    size="small"
    onClick={() => this.setState({ visibleSubgroups: [...allSubgroups] })}
    sx={{ cursor: "pointer" }}
  />
  <Chip
    label="Clear All"
    size="small"
    onClick={() => this.setState({ visibleSubgroups: [] })}
    sx={{ cursor: "pointer" }}
  />
</Stack>
            </>
          )}

{view === "breakdown" && (
  <>
    <Divider sx={{ my: 0.5 }} />
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
    >
      Filter by Demographics
    </Typography>

    {/* Reset button */}
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
      <Chip
        label="Reset Filters"
        color="secondary"
        onClick={() => {
          this.setState({ filters: {} }, () =>
            this.fetchData(this.state.question, {})
          );
        }}
        sx={{
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          borderRadius: "16px",
        }}
      />
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {Object.entries(this.state.filterOptions).map(([key, options]) => {
        // Determine how many filters are active
        const activeCount = Object.values(this.state.filters).filter(Boolean).length;
        const isDisabled =
          activeCount >= 2 && !this.state.filters[key]; // disable new ones if already 2 active

        return (
          <Box key={key} sx={{ opacity: isDisabled ? 0.5 : 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 0.5, color: "text.secondary" }}
            >
             {formatKeyLabel(key)}
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label="All"
                variant={!this.state.filters[key] ? "filled" : "outlined"}
                color={!this.state.filters[key] ? "primary" : "default"}
                onClick={() =>
                  !isDisabled && this.handleFilterChange(key, "")
                }
                sx={{ borderRadius: "16px" }}
              />

              {options.map((opt) => (
                <Chip
                  key={opt}
                  label={opt}
                  disabled={isDisabled && this.state.filters[key] !== opt}
                  variant={
                    this.state.filters[key] === opt ? "filled" : "outlined"
                  }
                  color={
                    this.state.filters[key] === opt ? "primary" : "default"
                  }
                  onClick={() =>
                    !isDisabled && this.handleFilterChange(key, opt)
                  }
                  sx={{
                    borderRadius: "16px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor:
                        this.state.filters[key] === opt
                          ? "primary.dark"
                          : "rgba(0,0,0,0.05)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  </>
)}

  </Box>

        </Paper>

        {/* Chart */}
        <Paper sx={{ flexGrow: 1, p: 3, borderRadius: 3, bgcolor: "#fff" }}>
          {view === "breakdown" ? (
             <>
            <Typography
                       variant="h5"
                       sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
                     >
                       {questionText}
                     </Typography>
           
                     <Typography
                       variant="subtitle2"
                       sx={{ mb: 3, color: "text.secondary", fontStyle: "italic" }}
                     >
                       Filtered by: {this.renderActiveFilters()}
                     </Typography>
           
                     {loading ? (
                       <Box
                         sx={{
                           display: "flex",
                           justifyContent: "center",
                           alignItems: "center",
                           height: 400,
                         }}
                       >
                         <CircularProgress />
                       </Box>
                     ) : (
                       <ResponsiveContainer width="100%" height="95%">
                         <LineChart
                           data={data}
                           margin={{ top: 20, right: 30, bottom: 60, left: 20 }}
                         >
                           <XAxis
                             dataKey="wave"
                             angle={-45}
                             textAnchor="end"
                             interval={0}
                             height={70}
                             tick={{ fontSize: 12 }}
                           />
                        <YAxis domain={yDomain} tickFormatter={(t) => `${t}%`} />
                        <Tooltip
             content={({ active, payload, label }) => {
               if (!active || !payload || !payload.length) return null;
           
               const ordered = [...payload].sort((a, b) => {
                   const ia = RESPONSE_ORDER.indexOf(a.name);
                   const ib = RESPONSE_ORDER.indexOf(b.name);
                   if (ia === -1 && ib === -1) return a.name.localeCompare(b.name);
                   if (ia === -1) return 1;
                   if (ib === -1) return -1;
                   return ia - ib;
                 });
           
                 return (
                   <div
                     style={{
                       background: "white",
                       border: "1px solid #ddd",
                       borderRadius: 8,
                       padding: "10px 12px",
                       boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                     }}
                   >
                     <strong style={{ display: "block", marginBottom: 6 }}>
                       {label}
                     </strong>
                     {ordered.map((entry) => {
                       const isNet = entry.name === "Net value";
                       return (
                         <div
                           key={entry.name}
                           style={{
                             color: isNet ? "#000" : entry.color,
                             fontSize: isNet ? 13.5 : 13,
                             lineHeight: "1.4em",
                             display: "flex",
                             justifyContent: "space-between",
                             alignItems: "center",
                             gap: 10,
                             fontWeight: isNet ? 700 : 400,
                             background: isNet ? "rgba(0,0,0,0.05)" : "transparent",
                             border: isNet ? "1px solid rgba(0,0,0,0.2)" : "none",
                             borderRadius: isNet ? 6 : 0,
                             padding: isNet ? "4px 6px" : "0",
                             marginTop: isNet ? 4 : 0,
                           }}
                         >
                           <span>{entry.name}</span>
                           <span>{entry.value.toFixed(1)}%</span>
                         </div>
                       );
                     })}
                   </div>
                 );
                 
             }}
           />
           <Legend
             verticalAlign="bottom"
             height={60}
             wrapperStyle={{ paddingTop: 10 }}
             content={({ payload }) => {
               if (!payload) return null;
           
               const ordered = [...payload].sort((a, b) => {
                 const ia = RESPONSE_ORDER.indexOf(a.value);
                 const ib = RESPONSE_ORDER.indexOf(b.value);
                 if (ia === -1 && ib === -1) return a.value.localeCompare(b.value);
                 if (ia === -1) return 1;
                 if (ib === -1) return -1;
                 return ia - ib;
               });
           
               return (
                 <ul
                   style={{
                     display: "flex",
                     flexWrap: "wrap",
                     justifyContent: "center",
                     listStyle: "none",
                     margin: 0,
                     padding: 0,
                   }}
                 >
                   {ordered.map((entry) => (
                     <li
                       key={entry.value}
                       style={{
                         marginRight: 15,
                         display: "flex",
                         alignItems: "center",
                         fontSize: 13,
                         color: "#444",
                       }}
                     >
                       <span
                         style={{
                           display: "inline-block",
                           width: 10,
                           height: 10,
                           borderRadius: "50%",
                           backgroundColor: entry.color,
                           marginRight: 6,
                         }}
                       />
                       {entry.value}
                     </li>
                   ))}
                 </ul>
               );
             }}
           />
           
           
           {(() => {
             const keys = Object.keys(data[0] || {}).filter((k) => k !== "wave");
             const orderedKeys = RESPONSE_ORDER.filter((key) => keys.includes(key));
           
             return orderedKeys.map((resp) => {
               const color =
                 LIKERT_COLOURS[resp] ||
                 (resp === "Net agreement" ? "#000000" : "#888888");
               const isNet = resp === "Net value";
           
               return (
                 <Line
                   key={resp}
                   type="monotone"
                   dataKey={resp}
                   stroke={color}
                   strokeWidth={isNet ? 3.2 : 2.2}
                   strokeDasharray={isNet ? "6 3" : undefined}
                   dot={{ r: isNet ? 0 : 3 }}
                   activeDot={{ r: isNet ? 0 : 5 }}
                   name={resp}
                   legendType={isNet ? "line" : "circle"}
                 />
               );
             });
           })()}
           
                         </LineChart>
                       </ResponsiveContainer>
                     
                    )}
    </>
          ) : (
            <>
            <Typography
  variant="h5"
  sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
>
  {QUESTION_TEXT.find((q) => q.key === question)?.label || question} - {this.state.netLabel}
</Typography>

<Typography
  variant="subtitle2"
  sx={{ mb: 3, color: "text.secondary", fontStyle: "italic" }}
>
  Split by: {splitBy.charAt(0).toUpperCase() + splitBy.slice(1)}
</Typography>

<ResponsiveContainer width="100%" height="95%">
<LineChart data={chartDataNet} margin={{ top: 20, right: 30, bottom: 60, left: 20 }}>
<XAxis
  dataKey="wave"
  type="category"
  ticks={chartDataNet.map((d) => d.wave)}
  allowDuplicatedCategory={false}
  interval={0}
  angle={-45}
  textAnchor="end"
  height={70}
  tick={{ fontSize: 12 }}
/>                <YAxis domain={yDomain} tickFormatter={(t) => `${t}%`} />
                <Tooltip
  content={({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const netLabel = this.state.netLabel || "Net value";
    const all = payload.find((p) => p.name === ALL_KEY)?.value ?? 0;
    const ordered = [...payload].sort((a, b) => {
      if (a.name === ALL_KEY) return -1;
      if (b.name === ALL_KEY) return 1;
      return a.name.localeCompare(b.name);
    });

    return (
      <div
        style={{
          background: "white",
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: "10px 12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: 180,
        }}
      >
        <strong style={{ display: "block", marginBottom: 6 }}>
          {netLabel}
        </strong>
        {ordered.map((entry) => {
          const isAll = entry.name === ALL_KEY;
          const delta = isAll ? 0 : entry.value - all;
          return (
            <div
              key={entry.name}
              style={{
                color: isAll ? "#000" : entry.color,
                fontWeight: isAll ? 700 : 500,
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <span>{entry.name}</span>
              <span>
                {entry.value.toFixed(1)}%
                {!isAll && (
                  <span style={{ marginLeft: 8, color: "#666", fontSize: 12 }}>
                    ({delta >= 0 ? "+" : ""}
                    {delta.toFixed(1)} vs All)
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  }}
/>

                <Legend />
                <Line
                  type="monotone"
                  dataKey={ALL_KEY}
                  name={ALL_KEY}
                  stroke={colorMap[ALL_KEY] || "#000"}
                  strokeWidth={3}
                  strokeDasharray="6 3"
                  dot={false}
                />
                {allSubgroups
                  .filter((sg) => visibleSubgroups.includes(sg))
                  .map((sg) => (
                    <Line
                      key={sg}
                      type="monotone"
                      dataKey={sg}
                      stroke={colorMap[sg]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
            </>
          )}
        </Paper>
      </Box>
    );
  }
}
